import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Briefcase, Home, Plus, Trash2, MessageSquare, Clock, CheckCircle, XCircle, AlertCircle, Mail, User } from 'lucide-react';
import { toast } from 'sonner';
import { API } from '../App';

const MisAnunciosPage = ({ user }) => {
  const navigate = useNavigate();
  const [anuncios, setAnuncios] = useState([]);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [unreadCount, setUnreadCount] = useState(0);

  useEffect(() => {
    if (user) {
      fetchMyAnuncios();
      fetchMyMessages();
    }
  }, [user]);

  const fetchMyAnuncios = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API}/anuncios/my`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setAnuncios(data);
      }
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchMyMessages = async () => {
    try {
      const token = localStorage.getItem('token');
      
      // Get unread count first
      const countResponse = await fetch(`${API}/my-messages/unread-count`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (countResponse.ok) {
        const countData = await countResponse.json();
        setUnreadCount(countData.unread_count);
      }

      // Get all messages
      const response = await fetch(`${API}/my-messages`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setMessages(data);
      }
    } catch (error) {
      console.error('Error:', error);
    }
  };

  const handleDelete = async (anuncioId) => {
    if (!confirm('¿Estás seguro de que quieres eliminar este anuncio?')) return;

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API}/anuncios/${anuncioId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        toast.success('Anuncio eliminado');
        setAnuncios(anuncios.filter(a => a.id !== anuncioId));
      } else {
        toast.error('Error al eliminar');
      }
    } catch (error) {
      toast.error('Error al eliminar');
    }
  };

  const getStatusBadge = (status) => {
    const config = {
      pending: { color: 'bg-amber-100 text-amber-700', icon: Clock, label: 'Pendiente' },
      approved: { color: 'bg-green-100 text-green-700', icon: CheckCircle, label: 'Aprobado' },
      rejected: { color: 'bg-red-100 text-red-700', icon: XCircle, label: 'Rechazado' }
    };
    const { color, icon: Icon, label } = config[status] || config.pending;
    return (
      <Badge className={`${color} flex items-center gap-1`}>
        <Icon className="w-3 h-3" />
        {label}
      </Badge>
    );
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('es-ES', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });
  };

  if (!user) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <Card className="max-w-md mx-4">
          <CardContent className="p-8 text-center">
            <AlertCircle className="w-16 h-16 text-amber-500 mx-auto mb-4" />
            <h2 className="text-2xl font-bold mb-2">Inicia sesión</h2>
            <p className="text-slate-600 mb-4">Necesitas una cuenta para ver tus anuncios</p>
            <Link to="/auth">
              <Button className="bg-red-600 hover:bg-red-700">Iniciar sesión</Button>
            </Link>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100">
      {/* Navigation */}
      <nav className="bg-white shadow-sm sticky top-0 z-40">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <Link to="/" className="flex items-center space-x-3">
              <Briefcase className="w-7 h-7 text-red-600" />
              <span className="text-xl font-bold bg-gradient-to-r from-red-600 to-red-700 bg-clip-text text-transparent">
                JobSuiza
              </span>
            </Link>

            <div className="flex items-center gap-2">
              <Button variant="ghost" onClick={() => navigate('/')} className="hidden sm:flex">
                <Home className="w-4 h-4 mr-2" />
                Inicio
              </Button>
              <Button variant="ghost" onClick={() => navigate('/anuncios')}>
                Tablón
              </Button>
              <Link to="/publicar-anuncio">
                <Button className="bg-red-600 hover:bg-red-700" size="sm">
                  <Plus className="w-4 h-4 mr-1" />
                  Nuevo
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </nav>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <h1 className="text-3xl font-bold text-slate-900 mb-6">Mis Anuncios</h1>

        <Tabs defaultValue="anuncios" className="space-y-6">
          <TabsList className="grid w-full grid-cols-2 max-w-md">
            <TabsTrigger value="anuncios">
              Mis Anuncios ({anuncios.length})
            </TabsTrigger>
            <TabsTrigger value="mensajes" className="relative">
              Mensajes Recibidos
              {unreadCount > 0 && (
                <span className="absolute -top-1 -right-1 bg-red-600 text-white text-xs w-5 h-5 rounded-full flex items-center justify-center">
                  {unreadCount}
                </span>
              )}
            </TabsTrigger>
          </TabsList>

          {/* Anuncios Tab */}
          <TabsContent value="anuncios">
            {loading ? (
              <div className="text-center py-12">
                <div className="w-12 h-12 border-4 border-red-600 border-t-transparent rounded-full animate-spin mx-auto"></div>
              </div>
            ) : anuncios.length === 0 ? (
              <Card>
                <CardContent className="p-8 text-center">
                  <MessageSquare className="w-16 h-16 text-slate-300 mx-auto mb-4" />
                  <h3 className="text-xl font-semibold text-slate-700 mb-2">No tienes anuncios</h3>
                  <p className="text-slate-500 mb-4">Publica tu primer anuncio en el tablón</p>
                  <Link to="/publicar-anuncio">
                    <Button className="bg-red-600 hover:bg-red-700">
                      <Plus className="w-4 h-4 mr-2" />
                      Publicar Anuncio
                    </Button>
                  </Link>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-4">
                {anuncios.map((anuncio) => (
                  <Card key={anuncio.id}>
                    <CardContent className="p-5">
                      <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-4">
                        <div className="flex-1">
                          <div className="flex flex-wrap items-center gap-2 mb-2">
                            {getStatusBadge(anuncio.status)}
                            <Badge variant="outline">{anuncio.category}</Badge>
                          </div>
                          <h3 className="font-semibold text-lg text-slate-900 mb-1">
                            {anuncio.title}
                          </h3>
                          <p className="text-slate-600 text-sm line-clamp-2 mb-2">
                            {anuncio.description}
                          </p>
                          <p className="text-sm text-slate-500">
                            Creado: {formatDate(anuncio.created_at)}
                          </p>
                          {anuncio.status === 'rejected' && anuncio.rejection_reason && (
                            <p className="text-sm text-red-600 mt-2">
                              <strong>Motivo del rechazo:</strong> {anuncio.rejection_reason}
                            </p>
                          )}
                        </div>
                        <div className="flex sm:flex-col gap-2">
                          {anuncio.status === 'approved' && (
                            <Button 
                              variant="outline" 
                              size="sm"
                              onClick={() => navigate(`/anuncios/${anuncio.id}`)}
                            >
                              Ver
                            </Button>
                          )}
                          <Button 
                            variant="outline" 
                            size="sm"
                            className="text-red-600 hover:text-red-700 hover:bg-red-50"
                            onClick={() => handleDelete(anuncio.id)}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>

          {/* Messages Tab */}
          <TabsContent value="mensajes">
            {messages.length === 0 ? (
              <Card>
                <CardContent className="p-8 text-center">
                  <Mail className="w-16 h-16 text-slate-300 mx-auto mb-4" />
                  <h3 className="text-xl font-semibold text-slate-700 mb-2">Sin mensajes</h3>
                  <p className="text-slate-500">Cuando alguien te contacte sobre un anuncio, aparecerá aquí.</p>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-4">
                {messages.map((msg) => (
                  <Card key={msg.id} className={!msg.is_read ? 'border-red-200 bg-red-50/30' : ''}>
                    <CardContent className="p-5">
                      <div className="flex items-start gap-4">
                        <div className="w-10 h-10 bg-slate-200 rounded-full flex items-center justify-center flex-shrink-0">
                          <User className="w-5 h-5 text-slate-500" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex flex-wrap items-center gap-2 mb-1">
                            <span className="font-semibold text-slate-900">{msg.sender_name}</span>
                            <span className="text-slate-400">•</span>
                            <span className="text-sm text-slate-500">{formatDate(msg.created_at)}</span>
                          </div>
                          <p className="text-sm text-slate-600 mb-2">
                            Sobre: <Link to={`/anuncios/${msg.anuncio_id}`} className="text-red-600 hover:underline">
                              {msg.anuncio_title}
                            </Link>
                          </p>
                          <p className="text-slate-700 whitespace-pre-wrap">{msg.message}</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default MisAnunciosPage;
